import React, { useEffect, useState, Fragment } from 'react';
import { Row, Col, message, Spin, Space, Button, Typography, Form, Empty } from 'antd';
import ListCard from 'Molecules/ListCard';
import HeadingChip from 'Molecules/HeadingChip';
import FormGroup from 'Molecules/FormGroup';
import { useDispatch, useSelector } from 'react-redux';
import { addKitchenQueueAlert, editKitchenQueueAlert, removeKitchenQueueAlert } from '../../../../../ducks/services'
import ActionButton from 'Molecules/ActionButton';
import { Popup } from 'Atoms/Popup';
import { LoadingOutlined } from '@ant-design/icons';
import { useForm } from 'react-hook-form';
import { useTranslate } from 'Translate';

const antIcon = <LoadingOutlined spin />;
const { Title } = Typography;

export default (props) => {
  const dispatch = useDispatch();
  const { id, data, reloadApi, allowedEdit, allowedDelete } = props;
  const { control, formState: { errors }, handleSubmit, setValue, getValues } = useForm();
  const [visible, setVisible] = useState(false);
  const [load, setLoad] = useState(false)
  const i18n = useTranslate();
  const { t } = i18n;
  
  const colName = [
    {
      title: t("System.alert_timer_secs"),
      dataIndex: 'alert_timer',
      key: 'alert_timer',
      className: 'enable-click',
      sorter: (a,b) => a.alert_timer - b.alert_timer
    },
    {
      title: t("System.color_code"),
      dataIndex: 'color_code',
      key: 'color_code',
      className: 'enable-click',
      sorter: (a,b) => a.color_code.localeCompare(b.color_code),
      render: (text) => <Space size={10}>
        <div style={{ width: '20px', height: '20px', backgroundColor: text?.includes("#") ? text : `#${text}` }}></div>
        {text}
      </Space>
    },
    {
      title: t("System.bg_color_opacity"),
      dataIndex: 'bg_color_opacity',
      key: 'bg_color_opacity',
      className: 'enable-click',
      sorter: (a,b) => a.bg_color_opacity - b.bg_color_opacity
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      width: 200,
      render: (text, record) =>
        <ActionButton
          title="kitchen_queue_alert"
          btnAction1={() => {
            setValue('id', record?.id);
            setValue('alert_timer', record?.alert_timer);
            setValue('color_code', record.color_code ? record.color_code : "" );
            setValue('bg_color_opacity', record?.bg_color_opacity);
            setVisible(true);
          }}
          recordId={record.id}
          onRemove={allowedDelete && onRemove}
          setLoading={setLoad}
          loading={load}
        />
    }
  ];

  const addNew = () => { 
    setVisible(true);
    setValue('id', null);
    setValue('alert_timer', '');
    setValue('color_code', '');
    setValue('bg_color_opacity', '');
  }

  const btnList = [
    {
      text: `+ ${t("New.kitchen_queue_alert")}`,
      classes: 'attendance-success',
      action: () => addNew(),
    },
  ];

  const formFields = [
    {
      name: 'id',
      label: '',
      type: 'input',
      hidden: true,
    },
    {
      name: 'alert_timer',
      label: t("System.alert_timer_secs"),
      req: true,
      placeholder: t("General.please_state"),
      type: 'input',
      twocol: false,
      colWidth: '0 1 100%',
      reqmessage: t("General.required"),
      number: true,
      nostep: true,
      static: !allowedEdit,
    },
    {
      name: 'color_code',
      label: t("System.color_code"),
      req: true,
      placeholder: t("General.please_state"),
      type: 'color',
      showText: true,
      twocol: false,
      colWidth: '0 1 100%',
      reqmessage: t("General.required"),
      static: !allowedEdit,
    },
    {
      name: 'bg_color_opacity',
      label: t("System.bg_color_opacity"),
      req: true,
      placeholder: t("General.please_state"),
      type: 'input',
      twocol: false,
      colWidth: '0 1 100%',
      reqmessage: t("General.required"),
      number: true,
      nostep: true,
      min: 0,
      max: 100,
      static: !allowedEdit,
      onChange: (e) => {
        if(+e.target.value > 100) setValue('bg_color_opacity', 100)
      }
    },
  ];

  const onRemove = async (recordId) => {
    setLoad(true);

    const payload = {
      kitchen_queue_alert_id: recordId,
      id: id,
      status_event: 'remove'
    }

    await removeKitchenQueueAlert(payload).then(res => {
      setLoad(false);
      if (res.data.code === 200) {
        message.success(t("Success.remove_kitchen_queue_alert"));
        reloadApi()
      }
    }).catch(e => {
      setLoad(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onFinish = async (val) => {
    setLoad(true);
    const payload = {
      alert_timer: val?.alert_timer,
      color_code: val?.color_code,
      bg_color_opacity: val?.bg_color_opacity,
      kitchen_queue_alert_id: val?.id,
      id: id
    }

    await (val?.id ? editKitchenQueueAlert : addKitchenQueueAlert)(payload).then(res => {
      setLoad(false);
      if (res.data.code === 200) {
        message.success(t(`Success.kitchen_queue_alert_${val?.id ? 'updated' : 'added'}`));
        setVisible(false);
        setValue('id', null);
        setValue('alert_timer', '');
        setValue('color_code', '');
        setValue('bg_color_opacity', '');
        reloadApi()
      } else {
        message.error(res.data.message);
      }
    }).catch(e => {
      setLoad(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  };

  const popup = {
    closable: false,
    visibility: visible,
    content: <Spin indicator={antIcon} size="large" spinning={load}>
      <Form layout="vertical" onFinish={handleSubmit(onFinish)}>
        <Space direction='vertical' size={15} className='w-100'>
          <Title level={4} className='m-0'>
            {getValues('id') ? t("General.edit") : t("General.add")} {t("Delete.kitchen_queue_alert")}
          </Title>
          <Row gutter={[10, 10]}>
            {formFields.map((item, idx) => (
              <Fragment key={idx}>
                <FormGroup item={item} control={control} errors={errors} />
              </Fragment>
            ))}
          </Row>
          <Row gutter={10} justify={'center'}>
            <Col span={12}><Button size='large' danger type="primary" className='w-100' onClick={() => {
              setVisible(false);
            }}>{t("General.cancel")}</Button></Col>
            {
              allowedEdit &&
              <Col span={12}><Button size='large' type="primary" className='green-btn w-100' htmlType='submit'>{t("General.confirm")}</Button></Col>
            }
          </Row>
        </Space>
      </Form>
    </Spin>,
    width: 536,
    onCancel: () => {
      setVisible(false);
      setValue('id', null);
      setValue('alert_timer', '');
      setValue('color_code', '');
      setValue('bg_color_opacity', '');
    },
  };

  const onClickRow = (record) => {
    return {
      onClick: (e) => {
        if (e.target.closest(".enable-click")) {
          setValue('id', record?.id);
          setValue('alert_timer', record?.alert_timer);
          setValue('color_code', record.color_code ? record.color_code : "" );
          setValue('bg_color_opacity', record?.bg_color_opacity);
          setVisible(true);
        }
      },
    };
  }

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <HeadingChip title={""} btnList={allowedEdit ? btnList : null} />
        </Col>
        <Col span={24} className="clickRow">
          <ListCard
            onRow={onClickRow}
            classes="nopad"
            ListData={data.kitchen_queue_alerts?.map(x => ({ ...x, key: x.id }))}
            ListCol={colName}
            pagination={false}
          />
        </Col>
      </Row>
      <Popup {...popup} />
    </>
  )
}